import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    Button,
    CircularProgress
} from '@mui/material';
import {
    UploadFileOutlined,
    CloseOutlined,
    CloudUploadOutlined,
    FileDownloadOutlined,
    InfoOutlined,
    DeleteOutline
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { uploadCreditOutstandingApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess, globalNotifyError } from 'views/utilities/AlertToast';

const UploadOutstandingModal = ({ open, onClose, onSuccess }) => {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleDownloadTemplate = () => {
        const templateData = [
            {
                email: 'employee1@corporate.com',
                current_outstanding: 2500.0
            },
            {
                email: 'employee2@corporate.com',
                current_outstanding: 0.0
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(templateData);
        worksheet['!cols'] = [{ wch: 30 }, { wch: 22 }];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Credit_Outstanding');
        XLSX.writeFile(workbook, 'Credit_Outstanding_Template.xlsx');
    };

    const handleClose = () => {
        if (isUploading) return;
        setUploadedFile(null);
        setUploadResult(null);
        setIsDragging(false);
        onClose();
    };

    const handleFileSelect = (file) => {
        if (!file) return;

        const validExtensions = ['.xlsx', '.xls', '.csv'];
        const fileName = file.name.toLowerCase();
        const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

        if (!isValid) {
            globalNotifyError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            globalNotifyError('File size exceeds 10MB limit');
            return;
        }

        setUploadedFile(file);
        setUploadResult(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();

        if (!uploadedFile) {
            globalNotifyError('Please select an Excel (.xlsx, .xls) or CSV file to upload');
            return;
        }

        const formData = new FormData();
        formData.append('file', uploadedFile);

        setIsUploading(true);
        setUploadResult(null);

        try {
            const response = await uploadCreditOutstandingApi(formData);
            const resData = response?.data;

            if (resData?.success || response?.status === 200) {
                const count = resData?.data?.processed_count ?? resData?.processed_count ?? '';
                const successMsg =
                    resData?.message || `Successfully processed credit outstanding${count ? ` for ${count} employees` : ''}.`;
                globalNotifySuccess(successMsg);

                if (resData?.data?.errors && resData.data.errors.length > 0) {
                    setUploadResult(resData.data);
                } else if (resData?.errors && resData.errors.length > 0) {
                    setUploadResult(resData);
                } else {
                    handleClose();
                }

                if (onSuccess) onSuccess();
            } else if (resData?.message) {
                globalNotifyError(resData.message);
            } else if (response?.response?.data?.message) {
                globalNotifyError(response.response.data.message);
            } else {
                globalNotifySuccess('Credit outstanding uploaded successfully');
                handleClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error uploading credit outstanding:', error);
            if (error?.response?.data?.message) {
                globalNotifyError(error.response.data.message);
            } else {
                handleCatchError(error);
            }
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: '#1d4ed8', display: 'flex' }}>
                        <UploadFileOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Upload Credit Outstanding
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Option A: Excel / CSV File Upload (multipart/form-data)
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={handleClose} disabled={isUploading}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleUploadSubmit}>
                <DialogContent sx={{ pt: 1.5 }}>
                    <Stack spacing={2.5}>
                        {/* Sample Format & Download Banner */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                                <InfoOutlined color="primary" fontSize="small" sx={{ mt: 0.3, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} color="text.primary">
                                        Expected Columns
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}
                                    >
                                        <b>email</b> (Column 0 by default) &nbsp;|&nbsp; <b>current_outstanding</b> (Column 1 by default)
                                    </Typography>
                                </Box>
                            </Stack>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlined fontSize="small" />}
                                onClick={handleDownloadTemplate}
                                sx={{
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    bgcolor: '#fff',
                                    borderColor: '#cbd5e1',
                                    color: '#1e40af',
                                    '&:hover': { bgcolor: '#eff6ff', borderColor: '#93c5fd' }
                                }}
                            >
                                Sample Template
                            </Button>
                        </Box>

                        {/* File Upload Dropzone */}
                        <Box
                            component="label"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragging ? '#1e40af' : uploadedFile ? '#16a34a' : 'divider',
                                borderRadius: 3,
                                p: 4,
                                textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: isDragging ? '#eff6ff' : uploadedFile ? '#f0fdf4' : '#fff',
                                display: 'block',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: uploadedFile ? '#16a34a' : '#1e40af',
                                    bgcolor: uploadedFile ? '#f0fdf4' : '#f8fafc'
                                }
                            }}
                        >
                            <input
                                type="file"
                                accept=".xlsx, .xls, .csv"
                                hidden
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        handleFileSelect(e.target.files[0]);
                                    }
                                }}
                            />
                            <CloudUploadOutlined
                                sx={{
                                    fontSize: 42,
                                    color: isDragging ? '#1e40af' : uploadedFile ? '#16a34a' : 'text.disabled',
                                    mb: 1
                                }}
                            />
                            {uploadedFile ? (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700} color="#15803d">
                                        {uploadedFile.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {(uploadedFile.size / 1024).toFixed(1)} KB — Click or drag to replace
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteOutline fontSize="small" />}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setUploadedFile(null);
                                        }}
                                        sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
                                    >
                                        Remove file
                                    </Button>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Choose an Excel (.xlsx, .xls) or CSV file
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Drag & drop file here or browse from device (Max 10MB)
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                        Header: multipart/form-data | Form Key: file
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Partial Errors Display */}
                        {uploadResult?.errors && uploadResult.errors.length > 0 && (
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                                <Typography variant="caption" fontWeight={700} color="#b91c1c" display="block" mb={0.5}>
                                    Upload Notices / Errors ({uploadResult.errors.length}):
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', color: '#dc2626' }}>
                                    {uploadResult.errors.map((err, idx) => (
                                        <li key={idx}>{typeof err === 'object' ? JSON.stringify(err) : String(err)}</li>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={isUploading} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isUploading || !uploadedFile}
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlined />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#1e40af',
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        {isUploading ? 'Uploading...' : 'Upload Outstanding'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

UploadOutstandingModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default UploadOutstandingModal;
